import { createContext, useContext, useEffect } from 'react';
import { site } from '@/content/site';

export const PageMetaContext = createContext<((meta: { title: string; description?: string }) => void) | null>(null);

/** Upsert a <meta> tag, creating it if the document does not have one yet. */
function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

/** Collect metadata during prerendering and update it on client navigation. */
export function usePageMeta(title: string, description?: string, _tabLabel?: string) {
  const collect = useContext(PageMetaContext);
  collect?.({ title: `${title} | ${site.name}`, description });
  useEffect(() => {
    const fullTitle = `${title} | ${site.name}`;

    document.title = fullTitle;

    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const url = `https://${site.domain}${path}`;

    // Canonical: tells search engines which URL is authoritative, so query
    // strings and trailing-slash variants do not read as duplicate pages.
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = url;

    setMeta('meta[property="og:url"]', 'property', 'og:url', url);
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle);
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle);

    if (!description) return;

    setMeta('meta[name="description"]', 'name', 'description', description);
    setMeta('meta[property="og:description"]', 'property', 'og:description', description);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description);
  }, [title, description]);
}
